import { useEffect } from 'react';

import type { ExperimentConfig, ResolvedExperimentData } from '@change-corgi/experiments/configs';

import { useTrackExperimentExposure } from './useTrackExperimentExposure';

type Options = Readonly<{
	/**
	 * Reset keys that re-fire the event when changed. Required when the same component instance
	 * is reused across logical contexts (e.g. SPA petition change with no remount), since
	 * variations are deterministic per user and would otherwise dedup the legitimate fire.
	 */
	resetKeys?: readonly unknown[];
	/**
	 * Also fires the legacy `experiment_started` event alongside `$exposure`, for consumers that
	 * still depend on it downstream. New usage should omit this — `$exposure` is the event of
	 * record for Amplitude Experiment.
	 */
	legacy?: boolean;
}>;

/**
 * Fires `$exposure` (and, when `legacy: true`, `experiment_started`) immediately on mount for the
 * given experiment. Deduped per mount via `useTrackOnce`. Behaves like `useTrackExperimentExposure`
 * but wires up the `useEffect` internally, so callers don't need to manage it.
 *
 * @example
 * useTrackExperimentExposureOnMount(experiment);
 */
export function useTrackExperimentExposureOnMount<E extends ExperimentConfig>(
	experiment: ResolvedExperimentData<E>,
	options?: Options,
): void {
	const trackExperimentExposure = useTrackExperimentExposure(experiment, options);

	useEffect(() => {
		void trackExperimentExposure();
	}, [trackExperimentExposure]);
}
